import moment from "moment";
import Icon from "@mdi/react";
import { useMemo, useRef, useState } from "react";
import {
    mdiFile, mdiPen, mdiPlus, mdiStar, mdiStarOutline,
} from "@mdi/js";
import { Link, useTitle } from "easy-page-router/react";

import { useHeaders, useSSRHook } from "../../ssrh/client";
import { useUser } from "../User";
import { FileIcon, Header, Loading, Remove } from "../components";
import { api } from "../services/api";

import { Download, DownloadFolder } from "../../types";
import { groupBy } from "lodash";
import Modal from "react-responsive-modal";
import Footer from "../components/Footer";
import { getFileName, toBase64 } from "../components/Download";

type DownloadsProps = {
    title: string,
    folder: DownloadFolder,
    background: "papers" | "writer" | "pens" | "mountain"
}

type DownloadStatus = {
    file: File,
    status: "pending" | "uploading" | "uploaded" | "error",
};

const emptyNew: Download = {
    _id: "",
    name: "",
    folder: "board",
    date: moment().format("YYYY-MM-DD"),
    isArchived: false,
    dateOfRemoval: null,
    file: {
        type: "EASY_DB_FILE",
        url: "",
    },
}

export default function Downloads({ title, folder, background }: DownloadsProps) {
    const user = useUser();

    const [downloads, error, isLoading, reload] = useSSRHook<Download[]>(`/api/downloads?folder=${encodeURIComponent(folder)}`);

    useTitle(title + " | TSO Krkonoše");
    useHeaders({
        title: title + " technického svazku obcí Krkonoše",
        description: "Kontakty na technický svazek obcí Krkonoše",
        image: window.location.origin + "/logo.png",
        canonical: window.location.origin + window.location.pathname,
    });

    const [category, setCategory] = useState("Aktivní");

    const inputRef = useRef<HTMLInputElement>(null);
    const [editDownload, setEditDownload] = useState<null | Download>(null);

    const [uploadFiles, setUploadFiles] = useState<null | DownloadStatus[]>(null);

    const [message, setMessage] = useState("");

    const categories = useMemo(() => {
        if (!downloads) return {};

        return groupBy(downloads, d => d.isArchived ? `Archiv ${moment(d.date).format("YYYY")}` : "Aktivní");
    }, [downloads]);

    async function handleFiles(fileList: FileList | null) {
        if (!fileList) return;

        const files: DownloadStatus[] = [];
        for (let i = 0; i < fileList.length; i++) {
            const file = fileList[i];
            if (file) files.push({ file, status: "pending" });
        }

        if (files.length === 0) return;
        if (files.length === 1) {
            // one file
            const file = files[0].file;
            const url = await toBase64(file);

            setMessage("");
            setEditDownload({
                ...(editDownload || emptyNew),
                name: file.name,
                folder,
                file: {
                    type: "EASY_DB_FILE",
                    url,
                },
            });
        } else {
            // multiple files
            for (const fileStatus of files) {
                fileStatus.status = "uploading";
                setUploadFiles(files);

                const file = fileStatus.file;
                const url = await toBase64(file);

                const [, error] = await api.addDownload({
                    _id: "",
                    name: file.name,
                    folder,
                    date: moment().format("YYYY-MM-DD"),
                    isArchived: false,
                    dateOfRemoval: null,
                    file: {
                        type: "EASY_DB_FILE",
                        url,
                    },
                });
                if (error) {
                    fileStatus.status = "error";
                } else {
                    fileStatus.status = "uploaded";

                }
                setUploadFiles(files);
            }

            reload();
        }
    }

    async function handleSave() {
        if (!editDownload) return;

        if (editDownload._id === "") {
            const [, error] = await api.addDownload(editDownload);
            if (error) {
                setMessage("Přidání souboru selhalo");
                return;
            }
            setMessage("Soubor přidán");
        } else {
            const [, error] = await api.updateDownload(editDownload);
            if (error) {
                setMessage("Ukládání změn selhalo");
                return;
            }
            setMessage("Soubor úspěšně uložen")
        }

        reload();
        setEditDownload(null);
    }

    async function handleRemove(downloadId: string) {
        await api.removeDownload({ downloadId });
        reload();
    }

    return (
        <>
            <main>
                <Header title={title} background={background}>
                    <Link to="/o-nas">O nás</Link>
                    <span className="space">-</span>
                    {title}
                </Header>
                <div className="content-split">
                    <div className="left">
                        <div className="content">
                            <section>
                                <div className="page">
                                    <h1 className="hidden">{title}</h1>
                                    {user.isAdmin && uploadFiles && <div style={{ paddingBottom: "40px" }}>
                                        <h3>Hromadné nahrávání souborů</h3>
                                        {uploadFiles.map((uf, i) => <div key={i}>
                                            <h4>{uf.file.name}</h4>
                                            <p>
                                                {uf.status === "pending" && "Čekám..."}
                                                {uf.status === "uploading" && "Nahrávám..."}
                                                {uf.status === "uploaded" && "Soubor úspěšně nahrán"}
                                                {uf.status === "error" && "Chyba při nahrávání souboru (Soubor může mít maximálně 10MB)"}
                                            </p>
                                        </div>)}
                                        <button onClick={() => setUploadFiles(null)}>Zavřít</button>
                                    </div>}

                                    <h2>Soubory ke stažení</h2>
                                    {folder === "board" && <p className="info">Originály povinně zveřejňovaných dokumentů jsou uložené k nahlédnutí na adrese kanceláře organizace.</p>}
                                    {user.isAdmin && message && <p className="alert">{message}</p>}

                                    {user.isAdmin && <div className="admin">
                                        <button onClick={() => inputRef.current?.click()}>
                                            <Icon path={mdiPlus} size={1} />Přidat soubor / soubory
                                        </button>
                                        <input
                                            className="hidden"
                                            ref={inputRef}
                                            type="file"
                                            multiple
                                            placeholder="Nahrát soubor"
                                            onChange={e => handleFiles(e.target.files)}
                                        />
                                        <Modal
                                            open={editDownload !== null}
                                            onClose={() => setEditDownload(null)}
                                            center
                                            styles={{ modal: { width: "500px" } }}
                                        >
                                            {message && <p className="alert">{message}</p>}
                                            {editDownload && <>
                                                <h2>Přidat soubor</h2>
                                                <button onClick={() => inputRef.current?.click()}>
                                                    <Icon path={mdiFile} size={1} />Nahrát jiný soubor
                                                </button>
                                                <p>Soubor může mít maximálně 10MB!</p>
                                                <label>Datum zveřejnění</label>
                                                <input
                                                    type="date"
                                                    placeholder="Datum zveřejnění"
                                                    value={editDownload.date}
                                                    onChange={(e) =>
                                                        setEditDownload({
                                                            ...editDownload,
                                                            date: moment(e.target.value).format("YYYY-MM-DD"),
                                                        })}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Název"
                                                    value={editDownload.name}
                                                    onChange={(e) =>
                                                        setEditDownload({ ...editDownload, name: e.target.value })}
                                                />
                                                <label>
                                                    Archivovaný
                                                    <input
                                                        type="checkbox"
                                                        checked={editDownload.isArchived}
                                                        onChange={(e) => setEditDownload({
                                                            ...editDownload,
                                                            dateOfRemoval: e.target.checked ? moment().format("YYYY-MM-DD") : null,
                                                            isArchived: e.target.checked,
                                                        })}
                                                    />
                                                </label>
                                                {editDownload.isArchived && <>
                                                    <br />
                                                    <label>Datum sejmutí</label>
                                                    <input
                                                        type="date"
                                                        placeholder="Datum sejmutí"
                                                        value={editDownload.dateOfRemoval || ""}
                                                        onChange={(e) => setEditDownload({
                                                            ...editDownload,
                                                            dateOfRemoval: moment(e.target.value).format("YYYY-MM-DD"),
                                                        })}
                                                    />
                                                </>}

                                                <button onClick={handleSave}>Uložit soubor</button>
                                            </>}
                                        </Modal>
                                    </div>}

                                    {isLoading && <Loading />}
                                    {error && (
                                        <p className="alert">
                                            Soubory se bohužel nepodařili načíst 🙁
                                        </p>
                                    )}
                                    {downloads && <ul className="download-list">
                                        {!categories[category] && <li>
                                            <a>Zatím zde nejsou žádné soubory ke stažení</a>
                                        </li>}
                                        {categories[category]?.map((d) => <li key={d._id}>
                                            <a href={d.file.url} target="__blank" download={getFileName(d)}>
                                                <FileIcon download={d} />
                                                <span className="name">{d.name}</span>
                                                <span className="date">
                                                    {!d.dateOfRemoval && moment(d.date).format("LL")}
                                                    {d.dateOfRemoval && <>
                                                        Zveřejněno: {moment(d.date).format("LL")}<br />
                                                        Sejmuto: {moment(d.dateOfRemoval).format("LL")}
                                                    </>}
                                                </span>
                                            </a>
                                            {user.isAdmin && <div className="control">
                                                <button className="icon" onClick={() => setEditDownload(d)}>
                                                    <Icon path={mdiPen} size={1} />
                                                </button>
                                                <Remove onRemove={() => handleRemove(d._id)} />
                                            </div>}
                                        </li>)}
                                    </ul>}
                                </div>
                            </section>
                        </div>
                    </div>
                    <div className="right">
                        <div className="content">
                            <section>
                                <div className="page">
                                    <h3>Kategorie</h3>
                                    {isLoading && <Loading />}
                                    {downloads && <ul className="cat-list">
                                        {Object.keys(categories).length === 0 && <li>
                                            Zatím zde nejsou žádné kategorie
                                        </li>}
                                        {Object.keys(categories)
                                            .sort((a, b) => {
                                                if (a === "Aktivní") return -1;
                                                if (b === "Aktivní") return 1;
                                                return b.localeCompare(a);
                                            })
                                            .map(c => <li
                                                key={c}
                                                className={c === category ? "current" : ""}
                                            >
                                                <a onClick={() => setCategory(c)}>
                                                    <Icon path={c === category ? mdiStar : mdiStarOutline} size={0.8} />
                                                    {c}
                                                </a>
                                            </li>)}
                                    </ul>}
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </>
    );
}
